import { formatKg, kgToG, type ReceiptRow, type StockRow } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/store.js';
import { request } from '../../sync/api.js';
import { syncNow } from '../../sync/engine.js';
import { enqueue } from '../../sync/outbox.js';

/**
 * Приёмка сырья от поставщика.
 *
 * Два веса вводятся оба: по накладной и по факту взвешивания. На склад идёт
 * фактический — по накладной могло приехать что угодно, а расхождение остаётся
 * видимым: это то место, где потом спорят.
 *
 * Документ уходит событием в очередь: сырьё принимают у ворот, где связь
 * пропадает, и ждать её, стоя у машины, незачем.
 */

interface StockResponse {
  stock: StockRow[];
}

export function ReceiptsScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const [receipts, setReceipts] = useState<ReceiptRow[] | null>(null);
  const [materials, setMaterials] = useState<StockRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [supplier, setSupplier] = useState('');
  const [waybill, setWaybill] = useState('');
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<Record<string, { waybill: string; actual: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [list, stock] = await Promise.all([
        request<{ receipts: ReceiptRow[] }>('/api/receipts', { method: 'GET' }, 20_000),
        request<StockResponse>('/api/stock', { method: 'GET' }, 20_000),
      ]);
      setReceipts(list.receipts);
      setMaterials(stock.stock);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить приёмки');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (): Promise<void> => {
    if (!user) return;

    const lines = Object.entries(rows)
      .filter(([, value]) => value.waybill.trim() !== '' || value.actual.trim() !== '')
      .map(([materialTypeId, value]) => {
        const byWaybill = Number((value.waybill || value.actual).replace(',', '.'));
        const actual = Number((value.actual || value.waybill).replace(',', '.'));
        return { materialTypeId, byWaybill, actual };
      });

    if (lines.length === 0) {
      setError('Добавьте хотя бы одну строку');
      return;
    }
    if (lines.some((line) => !Number.isFinite(line.byWaybill) || !Number.isFinite(line.actual))) {
      setError('Вес — число килограммов');
      return;
    }
    if (supplier.trim().length < 2 || waybill.trim().length === 0) {
      setError('Укажите поставщика и номер накладной');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const receiptId = crypto.randomUUID();
      await enqueue({
        type: 'MATERIAL_RECEIPT_RECORDED',
        aggregateType: 'RECEIPT',
        aggregateId: receiptId,
        userId: user.id,
        payload: {
          receiptId,
          at: new Date().toISOString(),
          supplier: supplier.trim(),
          waybillNumber: waybill.trim(),
          note: note.trim() || null,
          lines: lines.map((line) => ({
            materialTypeId: line.materialTypeId,
            qtyByWaybillG: kgToG(line.byWaybill),
            qtyActualG: kgToG(line.actual),
          })),
          photos: [],
        },
      });
      await syncNow();
      await load();
      setAdding(false);
      setSupplier('');
      setWaybill('');
      setNote('');
      setRows({});
      setNotice('Приёмка записана');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось записать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Приёмка сырья</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setNotice(null);
          }}
          className="mb-6 w-full rounded-xl bg-emerald-600 px-5 py-4 text-lg font-semibold text-white active:bg-emerald-700"
        >
          Принять машину
        </button>
      )}

      {adding && (
        <section className="mb-6 rounded-xl bg-card shadow-card p-4">
          <h2 className="mb-1 text-lg font-semibold">Новая приёмка</h2>
          <p className="mb-4 text-base text-dim">
            На склад приходуется фактический вес. Вес по накладной сохраняется рядом —
            по нему потом видно расхождение.
          </p>

          <div className="flex flex-col gap-3">
            <input
              value={supplier}
              onChange={(event) => setSupplier(event.target.value)}
              placeholder="Поставщик"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={waybill}
              onChange={(event) => setWaybill(event.target.value)}
              placeholder="Номер накладной"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Примечание (необязательно)"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />

            <div className="mt-2 grid grid-cols-[1fr_auto_auto] items-center gap-2 text-base text-dim">
              <span>Сырьё</span>
              <span className="w-24 text-center">накладная</span>
              <span className="w-24 text-center">факт</span>
            </div>
            {materials.map((material) => (
              <div
                key={material.materialTypeId}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-2"
              >
                <span className="text-base">{material.name}</span>
                <input
                  value={rows[material.materialTypeId]?.waybill ?? ''}
                  onChange={(event) =>
                    setRows({
                      ...rows,
                      [material.materialTypeId]: {
                        waybill: event.target.value,
                        actual: rows[material.materialTypeId]?.actual ?? '',
                      },
                    })
                  }
                  inputMode="decimal"
                  placeholder="кг"
                  className="w-24 rounded-xl bg-muted px-3 py-3 text-base tabular-nums"
                />
                <input
                  value={rows[material.materialTypeId]?.actual ?? ''}
                  onChange={(event) =>
                    setRows({
                      ...rows,
                      [material.materialTypeId]: {
                        waybill: rows[material.materialTypeId]?.waybill ?? '',
                        actual: event.target.value,
                      },
                    })
                  }
                  inputMode="decimal"
                  placeholder="кг"
                  className="w-24 rounded-xl bg-muted px-3 py-3 text-base tabular-nums"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              className="flex-1 rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {busy ? 'Записываю…' : 'Записать приёмку'}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-xl bg-muted px-5 py-4 text-lg font-semibold"
            >
              Отмена
            </button>
          </div>
        </section>
      )}

      <h2 className="mb-3 text-lg font-semibold">Последние приёмки</h2>
      <div className="flex flex-col gap-3">
        {receipts !== null && receipts.length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Пока пусто.</p>
        )}
        {(receipts ?? []).map((receipt) => (
          <article key={receipt.id} className="rounded-xl bg-card shadow-card p-4">
            <p className="text-lg font-semibold">{receipt.supplier}</p>
            <p className="text-base text-dim">
              накладная {receipt.waybillNumber} · {new Date(receipt.at).toLocaleDateString('ru-RU')}{' '}
              · {receipt.byUserName}
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {receipt.lines.map((line) => (
                <li key={line.materialTypeId} className="text-base text-body">
                  {line.name}: {formatKg(line.qtyActualG)} кг
                  {line.diffG !== 0 && (
                    <span className="text-amber-700">
                      {' '}
                      ({line.diffG > 0 ? '+' : ''}
                      {formatKg(line.diffG)} кг к накладной)
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {receipt.note && <p className="mt-1 text-base text-dim">{receipt.note}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}
